import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { validate, createRequiredValidator } from '@adsk/bim360-matrix-react-helpers/es/validate';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import deprecate from '@adsk/bim360-matrix-react-helpers/es/deprecate';

/**
 * @class InputValidation
 **/
export class InputValidation extends PureComponent {
  /**
   * @constructor InputValidation
   **/
  constructor(props) {
    super(props);

    if (
      props.validator &&
      ('required' in props.validator ||
        (Array.isArray(props.validator) &&
          props.validator.some(validator => 'required' in validator)))
    ) {
      deprecate(
        'props.validator.required is deprecated in `InputValidation`, please use props.required'
      );
    }
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const { validator, ignore, required, children, ...rest } = this.props;
    const requiredValidator = createRequiredValidator(
      required,
      this.props[TSymbolProp].translate('REQUIRED_MESSAGE')
    );

    const message = ignore
      ? null
      : validate(this.props.value, { validators: validator, requiredValidator });

    return (
      <InputValidationView {...rest} message={message}>
        {children(!message)}
      </InputValidationView>
    );
  }
}
InputValidation.Types = InputValidationView.Types;

InputValidation.defaultProps = {
  type: InputValidationView.Types.SUBTITLE
};

const validatorPropType = PropTypes.shape({
  /**
   * A validation message in case validation didn't succeed
   */
  message: PropTypes.string,
  /**
   * A validation function that takes a value and returns true or false whether it's valid or not.
   */
  validate: PropTypes.func,
  /**
   * Whether or not this value is required (can also be passed as its own prop). This property will be deprecated in the future.
   */
  required: PropTypes.bool
});

InputValidation.propTypes = {
  /**
   * A function that returns a node to render, gets a flag indicating whether or not the value is valid.
   * (valid) => node
   */
  children: PropTypes.func.isRequired,
  /**
   * A validator object to validate with.
   */
  validator: PropTypes.oneOfType([validatorPropType, PropTypes.arrayOf(validatorPropType)]),
  /**
   * The value to validate
   */
  value: PropTypes.any,
  /**
   * The type of validation to display, the possible values are InputValidationView.Types.POPOVER, InputValidationView.Types.SUBTITLE
   */
  type: PropTypes.oneOf(objectValues(InputValidationView.Types)),
  /**
   * Whether or not to currently ignore the validation
   */
  ignore: PropTypes.bool,
  /**
   * An object containing attributes to be spread on the validation span element
   */
  messageProps: PropTypes.object,
  /**
   * Whether or not this value is required (can also be passed in the validator itself)
   */
  required: PropTypes.bool,
  /**
   * ClassName to apply on the container
   */
  className: PropTypes.string,
  /**
   * Style to apply on the container
   */
  style: PropTypes.object,
  /**
   *  The locale of the default required message
   */
  locale: PropTypes.string,
  /**
   * Type props (effective when selected type is InputValidationView.Types.POPOVER)
   */
  typeProps: PropTypes.object
};

export default withLocale(
  InputValidation,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
